<script>
import Layout from "@/router/layouts/main";
import appConfig from "@/app.config";
import PageHeader from "@/components/page-header";

import Datepicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";

import { ref, defineAsyncComponent } from "vue";
import axios from "axios";
import Swal from "sweetalert2";
import moment from "moment";

/* ======================================
 * ASYNC COMPONENT
 * ====================================== */
const FormAddPeserta = defineAsyncComponent(() =>
  import("./add-perencanaan_peserta.vue")
);
const FormImportPeserta = defineAsyncComponent(() =>
  import("./add-import_perencanaan_peserta.vue")
);

export default {
  page: {
    title: "Edit Data Perencanaan Pembelajaran",
    meta: [{ name: "description", content: appConfig.description }],
  },

  components: {
    Layout,
    PageHeader,
    Datepicker,
    FormAddPeserta,
    FormImportPeserta,
  },

  data() {
    return {
      /* =====================================================
       * UI
       * ===================================================== */
      title: "Edit Data Perencanaan Pembelajaran",
      items: [
        { text: "Data Pembelajaran", href: "/" },
        { text: "Perencanaan", href: "/data-pembelajaran/perencanaan" },
        { text: "Edit Data Perencanaan Pembelajaran", active: true },
      ],

      id: this.$route.params.id,

      /* =====================================================
       * STATE
       * ===================================================== */
      isLoadingReferensi: true,
      isLoadingData: true,
      axiosCatchError: "",

      /* =====================================================
       * REFERENSI
       * ===================================================== */
      ref_strategi: [],
      pic_ref: [],

      /* =====================================================
       * FORM
       * ===================================================== */
      nomor_surat: null,
      kode: null,
      judul: null,
      dasar_pelaksanaan: null,
      strategi: null,
      durasi_he: 0,
      durasi_jp: null,
      tanggal_mulai: ref(new Date()),
      tanggal_selesai: ref(new Date()),
      angkatan: null,
      pratikum: "Tidak",
      pic: null,

      /* =====================================================
       * PESERTA
       * ===================================================== */
      peserta_pembelajaran: [],
      peserta_pembelajaran_id: [],
      peserta_delete: [],

      /* =====================================================
       * MODAL
       * ===================================================== */
      showFormAddPeserta: false,
      showFormImportPeserta: false,

      /* =====================================================
       * SUBMIT
       * ===================================================== */
      status_submit: null,
      isSubmitting: false,

      /* =====================================================
       * BASELINE (CEK DUPLIKAT)
       * ===================================================== */
      original_data: {
        kode: null,
        angkatan: null,
        tanggal_mulai: null,
      },
    };
  },

  mounted() {
    this.loadAll();
  },

  methods: {
    /* =====================================================
     * MASTER LOADER
     * ===================================================== */
    async loadAll() {
      this.axiosCatchError = "";
      this.isLoadingReferensi = true;
      this.isLoadingData = true;

      try {
        await Promise.all([
          this.loadReferensi(),
          this.loadDataEdit(),
        ]);
      } catch (e) {
        this.axiosCatchError =
          "Gagal memuat data. Silakan coba beberapa saat lagi.";
      } finally {
        this.isLoadingReferensi = false;
        this.isLoadingData = false;
      }
    },

    /* =====================================================
     * LOAD REFERENSI
     * ===================================================== */
    async loadReferensi() {
      const headers = {
        Accept: "application/json",
        Authorization: "Bearer " + localStorage.access_token,
      };

      const [strategi, pic] = await Promise.all([
        axios.get(
          process.env.VUE_APP_BACKEND_URL_VERSION + "referensi/ref-strategi",
          { headers }
        ),
        axios.get(
          process.env.VUE_APP_BACKEND_URL_VERSION +
            "referensi/pic-pembelajaran",
          { headers }
        ),
      ]);

      this.ref_strategi = strategi.data.data.referensi;
      this.pic_ref = pic.data.data.referensi;
    },

    /* =====================================================
     * LOAD DATA EDIT
     * ===================================================== */
    async loadDataEdit() {
      const res = await axios.get(
        process.env.VUE_APP_BACKEND_URL_VERSION +
          "transactions/pembelajaran",
        {
          params: { id: this.id },
          headers: {
            Accept: "application/json",
            Authorization: "Bearer " + localStorage.access_token,
          },
        }
      );

      const d = res.data.data.data_pembelajaran;

      this.nomor_surat = d.pbl_nomor_surat;
      this.kode = d.pbl_kode;
      this.judul = d.pbl_judul;
      this.dasar_pelaksanaan = d.pbl_dasar_pelaksanaan;
      this.durasi_he = d.pbl_durasi_he;
      this.durasi_jp = d.pbl_durasi_jp;
      this.tanggal_mulai = new Date(d.pbl_tgl_mulai);
      this.tanggal_selesai = new Date(d.pbl_tgl_selesai);
      this.angkatan = d.pbl_angkatan;
      this.pratikum = d.pbl_pratikum;
      this.status_submit = d.status_perencanaan;
      this.strategi = d.strategi;

      this.pic = {
        pic_id: d.pic_id,
        pic_user_id: d.pic_user_id,
        pic_keterangan:
          d.pic.mp_kategori +
          " - " +
          d.pic.user.username +
          " - " +
          d.pic.user.name,
      };

      this.original_data = {
        kode: d.pbl_kode,
        angkatan: d.pbl_angkatan,
        tanggal_mulai: moment(d.pbl_tgl_mulai).format("YYYY-MM-DD"),
      };

      d.pembelajaran_peserta.forEach((p) => {
        this.peserta_pembelajaran.push({
          peserta_id: p.pls_id,
          peserta_status: p.status_peserta,
          peserta_jabatan_lama: p.pls_jabatan_lama,
          peserta_unit_lama: p.pls_unit_lama,
          peserta_data: {
            user_id: p.user.user_id,
            username: p.user.username,
            name: p.user.name,
            jabatan_name: p.user.jabatan_name,
            unit_name: p.user.unit_name,
          },
        });
        this.peserta_pembelajaran_id.push(p.user.user_id);
      });
    },

    /* =====================================================
     * PESERTA (WAJIB – SEBELUMNYA HILANG)
     * ===================================================== */
    formTambahPeserta() {
      this.showFormAddPeserta = false;
      this.$nextTick(() => (this.showFormAddPeserta = true));
    },

    formImportPeserta() {
      this.showFormImportPeserta = false;
      this.$nextTick(() => (this.showFormImportPeserta = true));
    },

    initDataPeserta(dataPeserta) {
      if (!dataPeserta) return;

      const peserta = [].concat.apply([], JSON.parse(dataPeserta));

      peserta.forEach((v) => {
        if (!this.peserta_pembelajaran_id.includes(v.peserta_data.user_id)) {
          this.peserta_pembelajaran.push(v);
        }
      });

      this.peserta_pembelajaran_id =
        this.peserta_pembelajaran.map(
          (v) => v.peserta_data.user_id
        );

      this.showFormAddPeserta = false;
      this.showFormImportPeserta = false;
    },

    deleteRow(_, row) {
      this.peserta_delete.push(row);
      this.peserta_pembelajaran =
        this.peserta_pembelajaran.filter((r) => r !== row);
    },

    verifAll() {
      this.peserta_pembelajaran.forEach(
        (p) => (p.peserta_status = "TERVERIFIKASI")
      );
    },

    /* =====================================================
     * HELPER
     * ===================================================== */
    countDurasiSelesai() {
      let tgl = new Date(this.tanggal_mulai);
      let i = 0;

      while (i < this.durasi_he) {
        tgl.setDate(tgl.getDate() + 1);
        if (![0, 6].includes(tgl.getDay())) i++;
      }

      this.tanggal_selesai = new Date(tgl.setDate(tgl.getDate() - 1));
    },

    formatDate(date) {
      return moment(date).format("YYYY-MM-DD");
    },

    isKeyFieldChanged() {
      return (
        this.kode !== this.original_data.kode ||
        this.angkatan !== this.original_data.angkatan ||
        moment(this.tanggal_mulai).format("YYYY-MM-DD") !==
          this.original_data.tanggal_mulai
      );
    },

    validateBeforeSubmit() {
      const required = [
        this.nomor_surat,
        this.kode,
        this.judul,
        this.dasar_pelaksanaan,
        this.strategi,
        this.durasi_he,
        this.durasi_jp,
        this.angkatan,
        this.pic,
      ];

      if (required.some((v) => !v)) {
        Swal.fire("Peringatan", "Data wajib belum lengkap", "warning");
        return false;
      }
      return true;
    },

    /* =====================================================
     * SUBMIT
     * ===================================================== */
    async submitEdit() {
      if (this.isSubmitting) return;
      if (!this.validateBeforeSubmit()) return;

      this.isSubmitting = true;

      if (
        this.status_submit === "menunggu" &&
        this.isKeyFieldChanged()
      ) {
        const dup = await this.checkDuplicateEdit();
        if (dup) {
          this.isSubmitting = false;
          Swal.fire(
            "Duplikat",
            "Kode & Angkatan sudah digunakan perencanaan lain.",
            "warning"
          );
          return;
        }
      }

      this.StoreData();
    },

    async checkDuplicateEdit() {
      const res = await axios.get(
        process.env.VUE_APP_BACKEND_URL_VERSION +
          "transactions/pembelajaran/cek-angkatan",
        {
          params: {
            id: this.id,
            kode: this.kode,
            angkatan: this.angkatan,
            tanggal_mulai: moment(this.tanggal_mulai).format("YYYY-MM-DD"),
          },
          headers: {
            Accept: "application/json",
            Authorization: "Bearer " + localStorage.access_token,
          },
        }
      );

      return res.data.data.status === true;
    },

    StoreData() {
      axios
        .put(
          process.env.VUE_APP_BACKEND_URL_VERSION +
            "transactions/pembelajaran",
          {
            id: this.id,
            nomor_surat: this.nomor_surat,
            kode: this.kode,
            judul: this.judul,
            dasar_pelaksanaan: this.dasar_pelaksanaan,
            strategi: this.strategi?.ms_name,
            strategi_id: this.strategi?.ms_id,
            durasi_he: this.durasi_he,
            durasi_jp: this.durasi_jp,
            tanggal_mulai: moment(this.tanggal_mulai).format("YYYY-MM-DD"),
            tanggal_selesai: moment(this.tanggal_selesai).format("YYYY-MM-DD"),
            angkatan: this.angkatan,
            pratikum: this.pratikum,
            pic: this.pic?.pic_id,
            pic_user_id: this.pic?.pic_user_id,
            peserta: JSON.stringify(this.peserta_pembelajaran),
            peserta_delete: JSON.stringify(this.peserta_delete),
            status_submit: this.status_submit,
            user_id: localStorage.getItem("session_user_id"),
          },
          {
            headers: {
              Accept: "application/json",
              Authorization: "Bearer " + localStorage.access_token,
            },
          }
        )
        .then(() => {
          Swal.fire({
            icon: "success",
            title: "Berhasil",
            text: "Data berhasil disimpan",
            timer: 2000,
            showConfirmButton: false,
          }).then(() =>
            this.$router.push({ name: "all-perencanaan-pembelajaran" })
          );
        })
        .finally(() => (this.isSubmitting = false));
    },

    changeStatusSubmit(type) {
      this.status_submit = type;
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <!-- ================= LOADING ================= -->
    <div
      v-if="isLoadingReferensi || isLoadingData"
      class="d-flex flex-column align-items-center justify-content-center py-5"
    >
      <b-spinner variant="primary" />
      <div class="text-muted mt-2">
        Memuat data perencanaan, mohon tunggu…
      </div>
    </div>

    <!-- ================= ERROR ================= -->
    <div v-else-if="axiosCatchError" class="container mt-3">
      <div class="alert alert-danger text-center">
        <i class="mdi mdi-alert-circle-outline me-1"></i>
        {{ axiosCatchError }}
        <div class="mt-2">
          <button class="btn btn-sm btn-danger" @click="loadAll">
            <i class="mdi mdi-refresh"></i> Coba Muat Ulang
          </button>
        </div>
      </div>
    </div>

    <!-- ================= FORM ================= -->
    <b-form
      v-else
      class="p-2"
      @submit.prevent="submitEdit"
    >
      <div class="row">
        <div class="col-lg-12">
          <div class="card">
            <div class="card-body">
              <div class="row">

                <!-- ================= FORM PERENCANAAN ================= -->
                <div class="col-12">
                  <b-card
                    class="border border-primary"
                    header-class="bg-transparent border-primary"
                  >
                    <template #header>
                      <h5 class="my-0 text-primary">
                        <i class="mdi mdi-calendar-edit me-3"></i>
                        Formulir Perencanaan Pembelajaran
                      </h5>
                    </template>

                    <div class="card-body p-0">
                      <div class="row">

                        <!-- ================= KIRI ================= -->
                        <div class="col-6">
                          <b-form-group>
                            <template #label>
                              Nomor Surat <span class="text-danger">*</span>
                            </template>
                            <b-form-input v-model="nomor_surat" />
                          </b-form-group>

                          <b-form-group>
                            <template #label>
                              Kode Pembelajaran <span class="text-danger">*</span>
                            </template>
                            <b-form-input v-model="kode" />
                          </b-form-group>

                          <b-form-group>
                            <template #label>
                              Judul <span class="text-danger">*</span>
                            </template>
                            <b-form-input v-model="judul" />
                          </b-form-group>

                          <b-form-group>
                            <template #label>
                              Dasar Pelaksanaan <span class="text-danger">*</span>
                            </template>
                            <b-form-input v-model="dasar_pelaksanaan" />
                          </b-form-group>

                          <b-form-group>
                            <template #label>
                              Strategi <span class="text-danger">*</span>
                            </template>
                            <v-select
                              class="form-control p-0"
                              :options="ref_strategi"
                              label="ms_name"
                              v-model="strategi"
                              placeholder="Pilih Strategi"
                            />
                          </b-form-group>

                          <div class="row">
                            <div class="col-6">
                              <b-form-group>
                                <template #label>
                                  Durasi HE <span class="text-danger">*</span>
                                </template>
                                <input
                                  type="number"
                                  class="form-control"
                                  v-model="durasi_he"
                                  @change="countDurasiSelesai"
                                />
                              </b-form-group>
                            </div>

                            <div class="col-6">
                              <b-form-group>
                                <template #label>
                                  Durasi JP <span class="text-danger">*</span>
                                </template>
                                <b-form-input
                                  type="number"
                                  v-model="durasi_jp"
                                />
                              </b-form-group>
                            </div>
                          </div>

                          <div class="row">
                            <div class="col-6">
                              <b-form-group>
                                <template #label>
                                  Tanggal Mulai <span class="text-danger">*</span>
                                </template>
                                <datepicker
                                  v-model="tanggal_mulai"
                                  :first-day-of-week="1"
                                  :disabled-week-days="[0, 6]"
                                  :format="formatDate"
                                  confirm
                                  @internal-model-change="countDurasiSelesai"
                                />
                              </b-form-group>
                            </div>

                            <div class="col-6">
                              <b-form-group>
                                <template #label>
                                  Tanggal Selesai <span class="text-danger">*</span>
                                </template>
                                <datepicker
                                  v-model="tanggal_selesai"
                                  :first-day-of-week="1"
                                  :disabled-week-days="[0, 6]"
                                  :format="formatDate"
                                  confirm
                                />
                              </b-form-group>
                            </div>
                          </div>
                        </div>

                        <!-- ================= KANAN ================= -->
                        <div class="col-6">
                          <b-form-group>
                            <template #label>
                              PIC <span class="text-danger">*</span>
                            </template>
                            <v-select
                              class="form-control p-0"
                              :options="pic_ref"
                              label="pic_keterangan"
                              v-model="pic"
                              placeholder="Pilih PIC"
                            />
                          </b-form-group>

                          <b-form-group>
                            <template #label>
                              Angkatan <span class="text-danger">*</span>
                            </template>
                            <b-form-input v-model="angkatan" />
                          </b-form-group>

                          <b-form-group>
                            <template #label>
                              Praktikum <span class="text-danger">*</span>
                            </template>
                            <select class="form-select" v-model="pratikum">
                              <option value="Tidak">Tidak</option>
                              <option value="Ya">Ya</option>
                            </select>
                          </b-form-group>
                        </div>

                      </div>
                    </div>
                  </b-card>
                </div>

                <!-- ================= PESERTA ================= -->
                <div class="col-12 mt-3">
                  <b-card class="border border-success">
                    <template #header>
                      <div class="d-flex justify-content-between">
                        <h5 class="text-success mb-0">
                          <i class="mdi mdi-account-multiple me-2"></i>
                          Peserta Pembelajaran
                          <small class="text-muted">(Opsional)</small>
                        </h5>
                        <div>
                          <button
                            type="button"
                            class="btn btn-success btn-sm"
                            @click="formTambahPeserta"
                          >
                            <i class="mdi mdi-plus"></i> Tambah
                          </button>
                          <button
                            type="button"
                            class="btn btn-info btn-sm ms-1"
                            @click="formImportPeserta"
                          >
                            <i class="mdi mdi-file-excel"></i> Import
                          </button>
                          <button
                            class="btn btn-primary btn-sm ms-1"
                            @click="verifAll"
                            :disabled="!peserta_pembelajaran.length"
                          >
                            <i class="mdi mdi-check-all"></i> Verif All
                          </button>
                        </div>
                      </div>
                    </template>

                    <div class="alert alert-info mb-2">
                      Data peserta <b>tidak wajib</b> pada tahap perencanaan.
                    </div>

                    <div class="table-responsive">
                      <table class="table table-bordered mb-0">
                        <thead class="bg-dark text-white text-center">
                          <tr>
                            <th>No</th>
                            <th>NIP</th>
                            <th>Nama</th>
                            <th>Status</th>
                            <th>Verif</th>
                            <th>Aksi</th>
                          </tr>
                        </thead>

                        <tbody>
                          <tr v-if="!peserta_pembelajaran.length">
                            <td colspan="6" class="text-center text-muted py-4">
                              Belum ada peserta
                            </td>
                          </tr>

                          <tr
                            v-for="(row, i) in peserta_pembelajaran"
                            :key="i"
                          >
                            <td>{{ i + 1 }}</td>
                            <td>{{ row.peserta_data.username }}</td>
                            <td>{{ row.peserta_data.name }}</td>
                            <td class="text-center">
                              <span class="badge badge-soft-success">OK</span>
                            </td>
                            <td class="text-center">
                              <b-form-checkbox
                                v-model="row.peserta_status"
                                value="TERVERIFIKASI"
                                unchecked-value="BELUM TERVERIFIKASI"
                              />
                            </td>
                            <td class="text-center">
                              <button
                                class="btn btn-danger btn-sm"
                                @click="deleteRow(i, row)"
                              >
                                <i class="fa fa-minus"></i>
                              </button>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </b-card>
                </div>

                <!-- ================= ACTION ================= -->
                <div class="col-12 text-end mt-3">
                  <router-link
                    :to="{ name: 'all-perencanaan-pembelajaran' }"
                    class="btn btn-secondary"
                  >
                    Kembali
                  </router-link>

                  <b-button
                    class="ms-2"
                    variant="warning"
                    type="submit"
                    :disabled="isSubmitting"
                    @click="changeStatusSubmit('draft')"
                  >
                    Simpan Draft
                  </b-button>

                  <b-button
                    class="ms-2"
                    variant="primary"
                    type="submit"
                    :disabled="isSubmitting"
                    @click="changeStatusSubmit('menunggu')"
                  >
                    Simpan & Ajukan
                  </b-button>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
    </b-form>

    <!-- ================= MODAL ================= -->
    <b-modal v-model="showFormAddPeserta" size="lg" hide-footer>
      <form-add-peserta @init-data-peserta="initDataPeserta" />
    </b-modal>

    <b-modal v-model="showFormImportPeserta" size="lg" hide-footer>
      <form-import-peserta @init-data-peserta="initDataPeserta" />
    </b-modal>
  </Layout>
</template>

